import { cn } from '@listening/utils/react-helpers';
import IconPause from '@listening/assets/astro/icons/icon-pause-white.svg?react';
import IconPlay from '@listening/assets/astro/icons/icon-play.svg?react';
import avatarFrame1 from '@listening/assets/astro/imgs/avatar-frames/avatar-1.png';
import avatarFrame2 from '@listening/assets/astro/imgs/avatar-frames/avatar-2.png';
import avatarFrame5 from '@listening/assets/astro/imgs/avatar-frames/avatar-5.png';

import * as m from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as reactUseAudioPlayer from 'react-use-audio-player';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: 'HeroVoices' });

// Map voice names to their sample audio files per locale
const voiceSampleMap: Record<string, Record<string, string>> = {
  en: {
    Biology: '/voice-samples/will_biology.mp3',
    History: '/voice-samples/scarlett_history.mp3',
    'Comp Sci': '/voice-samples/dan_computer-science.mp3',
  },
  es: {
    Biology: '/voice-samples/male-es-bio-sample.mp3',
    History: '/voice-samples/female-es-history-sample.mp3',
    'Comp Sci': '/voice-samples/male-es-cs-sample.mp3',
  },
};

function getVoiceSample(key: string): string {
  const locale = getLocale();
  return voiceSampleMap[locale]?.[key] ?? voiceSampleMap.en[key] ?? '';
}

const voices = [
  {
    key: 'Biology',
    name: () => m.homepage_hero_voice_biology(),
    avatar: avatarFrame1,
    description: 'Listen to biology papers with natural voice',
  },
  {
    key: 'History',
    name: () => m.homepage_hero_voice_history(),
    avatar: avatarFrame2,
    description: 'Experience history papers with human-like narration',
  },
  {
    key: 'Comp Sci',
    name: () => m.homepage_hero_voice_comp_sci(),
    avatar: avatarFrame5,
    description: 'Computer science papers read naturally',
  },
];

// Generate pseudo-random but consistent heights based on voice name
const barHeights = (voiceName: string) => {
  const seed = voiceName
    .split('')
    .reduce((acc, char) => acc + (char.codePointAt(0) ?? 0), 0);
  return Array.from({ length: 24 }, (_, index) => {
    const value = Math.sin(seed + index) * 10_000;
    return Math.floor(Math.abs(value) % 12) + 4;
  });
};

const AudioBars = ({
  isPlaying,
  voiceName,
  onComplete,
  usingRealAudio,
  duration,
}: {
  isPlaying: boolean;
  voiceName: string;
  onComplete: () => void;
  usingRealAudio: boolean;
  duration: number;
}) => {
  const [activeBarIndex, setActiveBarIndex] = useState(-1);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const heights = barHeights(voiceName);
  if (!isPlaying && activeBarIndex !== -1) setActiveBarIndex(-1);

  // Handle animation when playing
  useEffect(() => {
    if (!isPlaying) return;

    // Calculate interval based on audio duration or use default for visual simulation
    const intervalDuration =
      usingRealAudio && duration > 0
        ? (duration * 1000) / 24 // Divide total duration by number of bars
        : 300; // Default interval for visual simulation

    const updateBarIndex = () => {
      setActiveBarIndex((prev) => {
        const next = prev + 1;
        if (next >= 24) {
          // Only complete if we're not using real audio
          if (!usingRealAudio) {
            if (intervalIdRef.current) {
              clearInterval(intervalIdRef.current);
            }
            // Schedule onComplete callback to run after state update
            timeoutIdRef.current = setTimeout(onComplete, 0);
            return -1;
          }
          return 23; // Stay at the last bar for real audio
        }
        return next;
      });
    };

    intervalIdRef.current = setInterval(updateBarIndex, intervalDuration);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [isPlaying, onComplete, usingRealAudio, duration]);

  return (
    <div
      aria-label={`Audio visualization for ${voiceName}`}
      aria-valuemax={24}
      aria-valuemin={0}
      aria-valuenow={activeBarIndex + 1}
      className='flex h-4 items-center'
      role='progressbar'
    >
      {heights.map((height, index) => (
        <div
          aria-hidden='true'
          className={cn(
            'w-px rounded-full transition-colors duration-300',
            isPlaying && index <= activeBarIndex
              ? 'bg-blue-500'
              : 'bg-steel-140',
          )}
          key={`${voiceName}-bar-${String(index)}`}
          style={{
            height: `${String(height)}px`,
            marginRight: index === heights.length - 1 ? '0' : '1px',
          }}
        />
      ))}
    </div>
  );
};

export function HeroVoices() {
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const audioPlayer = reactUseAudioPlayer.useAudioPlayer();

  // Track if we're using real audio or visual simulation
  const [usingRealAudio, setUsingRealAudio] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);

  // This function will force stop the audio and dispatch an event
  const forceStopAudio = useCallback(() => {
    try {
      // Stop using the audioPlayer API
      audioPlayer.pause();
    } catch (error) {
      moduleLogger.error({ err: error }, 'Error in forceStopAudio');
    }

    // Clear our state regardless of success/failure
    setPlayingVoice(null);
    setUsingRealAudio(false);

    // Dispatch an event to signal that audio has stopped
    window.dispatchEvent(new CustomEvent('audioStopped'));
  }, [audioPlayer]);

  // Listen for the stopAllWavesurfers event to stop our audio too
  useEffect(() => {
    const handleStopAllAudio = () => {
      if (playingVoice && usingRealAudio) {
        forceStopAudio();
      }
    };

    window.addEventListener('stopAllWavesurfers', handleStopAllAudio);
    return () => {
      window.removeEventListener('stopAllWavesurfers', handleStopAllAudio);
    };
  }, [playingVoice, usingRealAudio, forceStopAudio]);

  // Handle playing a voice sample
  const handlePlayVoice = (voiceName: string) => {
    // If this voice is already playing, stop it
    if (playingVoice === voiceName) {
      forceStopAudio();
      return;
    }

    // If another voice is playing, stop it first
    if (playingVoice) {
      // Set up the event listener for the audioStopped event
      const handleAudioStopped = () => {
        playAudioSample(voiceName);
        // Clean up the listener after it's used
        window.removeEventListener('audioStopped', handleAudioStopped);
      };

      // Add the listener before stopping the current audio
      window.addEventListener('audioStopped', handleAudioStopped);

      // Stop the current audio
      forceStopAudio();
    } else {
      // Nothing playing, so play immediately
      playAudioSample(voiceName);
    }
  };

  // Play an audio sample using a more controlled approach
  const playAudioSample = (voiceName: string) => {
    // Set the new playing voice immediately for UI responsiveness
    setPlayingVoice(voiceName);

    // Get the sample path
    const samplePath = getVoiceSample(voiceName);
    if (!samplePath) {
      setUsingRealAudio(false);
      return;
    }

    try {
      // Dispatch event to stop other audio
      const stopEvent = new CustomEvent('stopAllWavesurfers');
      window.dispatchEvent(stopEvent);

      // Use the audioPlayer API
      audioPlayer.load(samplePath, {
        format: 'mp3',
        autoplay: true,
        html5: true,
        onload: () => {
          if (playingVoice === voiceName) {
            setUsingRealAudio(true);
            setAudioDuration(audioPlayer.duration || 0);
          }
        },
        onend: () => {
          setPlayingVoice(null);
          setUsingRealAudio(false);
          setAudioDuration(0);
        },
      });
    } catch (error) {
      moduleLogger.error({ err: error }, 'Error in playAudioSample');
      // Fall back to visual simulation
      setUsingRealAudio(false);
    }
  };

  return (
    <div
      aria-label='Voice samples'
      className='mt-12 flex w-full flex-col items-center justify-center gap-4 px-4 lg:flex-row lg:gap-6'
      role='region'
    >
      {voices.map((voice) => (
        <div
          aria-label={`${voice.name()} voice sample`}
          className='flex w-full max-w-[360px] items-center justify-between gap-3 rounded-[500px] border-[5px] border-steel-140 bg-white py-3 pr-6 pl-3 shadow-xs transition-all duration-200 hover:border-steel-220 hover:shadow-md sm:gap-6 sm:pr-8 lg:w-auto'
          key={voice.key}
          role='article'
        >
          <div className='flex items-center gap-2 sm:gap-3'>
            <img
              alt={`${voice.name()} voice avatar`}
              className='size-7 rounded-full sm:size-8'
              height={32}
              loading='eager'
              src={voice.avatar}
              width={32}
            />
            <span
              aria-label={voice.description}
              className='text-sm font-medium text-nowrap sm:text-base'
            >
              {voice.name()}
            </span>
          </div>

          <AudioBars
            duration={audioDuration}
            isPlaying={playingVoice === voice.key}
            onComplete={() => {
              // Only handle completion for visual simulation
              if (!usingRealAudio && playingVoice === voice.key) {
                setPlayingVoice(null);
              }
            }}
            usingRealAudio={usingRealAudio && playingVoice === voice.key}
            voiceName={voice.key}
          />

          <button
            aria-label={`${playingVoice === voice.key ? 'Pause' : 'Play'} ${voice.name()} voice sample`}
            aria-pressed={playingVoice === voice.key}
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-full bg-black',
              'transition-transform hover:scale-110 active:scale-95',
            )}
            onClick={() => {
              handlePlayVoice(voice.key);
            }}
            type='button'
          >
            {playingVoice === voice.key ? (
              <IconPause aria-hidden='true' className='size-2 text-white' />
            ) : (
              <IconPlay aria-hidden='true' className='size-3 text-white' />
            )}
          </button>
        </div>
      ))}
    </div>
  );
}
